// The merges into an account or contact (MOTHER-180; Cameron's card on OLYAA-131), read from
// GET /api/merge/history (MOTHER-178, apps/server/src/mergeRecords.mjs) for the record page's
// "Merged Records" section (MergedRecordsSection.tsx). Admins only, as the route is: anyone
// else is never asked for it. A failed load shows nothing, as History does.
import { useEffect, useState } from 'react';

import type { ObjectKey } from '../navigation';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** The objects whose merges the route lists, as it names them. */
export const MERGE_HISTORY_OBJECTS = ['accounts', 'contacts'] as const;
export type MergeHistoryObject = (typeof MERGE_HISTORY_OBJECTS)[number];

export function hasMergeHistory(object: string): object is MergeHistoryObject {
  return (MERGE_HISTORY_OBJECTS as readonly string[]).includes(object);
}

/** One table's stored counts: rows moved to the kept record, and duplicates dropped. */
export interface MovedCount {
  moved: number;
  dropped: number;
}

/** One merge into the record, as the route sends it. */
export interface MergeEntry {
  merged_id: string;
  merged_name: string | null;
  merged_by_name: string | null;
  merged_at: string;
  moved: Record<string, MovedCount>;
}

/** The route's answer: the latest merges, newest first, and how many there are in all. */
export interface MergeHistory {
  object: MergeHistoryObject;
  id: string;
  total_merges: number;
  truncated?: boolean;
  merges: MergeEntry[];
}

/** The route lists at most this many merges (the server's MAX_HISTORY). */
export const MERGE_HISTORY_LIMIT = 50;

/** Who merged, when the route sends no name. */
export const UNKNOWN_MERGER = 'Unknown';

/** "Moved" when every count is 0. */
export const NOTHING_MOVED = 'Nothing moved';

export function mergeHistoryUrl(object: MergeHistoryObject, id: string): string {
  return `/api/merge/history?${new URLSearchParams({ object, id }).toString()}`;
}

/** The merges into one record; throws on any answer but a 200 with a list. */
export async function fetchMergeHistory(
  fetcher: Fetch,
  object: MergeHistoryObject,
  id: string,
  init: { signal?: AbortSignal } = {},
): Promise<MergeHistory> {
  const response = await fetcher(mergeHistoryUrl(object, id), init);
  if (!response.ok) throw new Error(`Merge history answered ${response.status}.`);
  const body = (await response.json()) as MergeHistory;
  if (!body || !Array.isArray(body.merges) || typeof body.total_merges !== 'number') throw new Error('Merge history was not understood.');
  return body;
}

/**
 * Each moved table's words, singular and plural, keyed by the table names the merge stores
 * (ACCOUNT_MOVES and CONTACT_MOVES in apps/server/src/mergeRecords.mjs), in the order a
 * summary lists them.
 */
const MOVED_LABELS: Record<string, readonly [string, string]> = {
  contacts: ['contact', 'contacts'],
  opportunities: ['opportunity', 'opportunities'],
  opportunity_contact: ['opportunity contact role', 'opportunity contact roles'],
  projects: ['project', 'projects'],
  quote: ['quote', 'quotes'],
  sales_order: ['sales order', 'sales orders'],
  invoice: ['invoice', 'invoices'],
  billing_account: ['billing account', 'billing accounts'],
  contract: ['contract', 'contracts'],
  asset: ['asset', 'assets'],
  revenue_period: ['revenue period', 'revenue periods'],
  resource_project_period: ['resource period', 'resource periods'],
  resource_assignment: ['resource assignment', 'resource assignments'],
  time_entries: ['time entry', 'time entries'],
  work_item: ['work item', 'work items'],
  meetings: ['meeting', 'meetings'],
  meeting_accounts: ['meeting link', 'meeting links'],
  meeting_contacts: ['meeting attendance', 'meeting attendances'],
  meeting_link_suggestions: ['meeting suggestion', 'meeting suggestions'],
  transcript_chunks: ['transcript excerpt', 'transcript excerpts'],
  audience_members: ['audience membership', 'audience memberships'],
  activity_links: ['activity link', 'activity links'],
  document_links: ['document link', 'document links'],
};
const LABEL_ORDER = Object.keys(MOVED_LABELS);

/** A table's words for a count: from MOVED_LABELS, else its stored name with spaces, lower-cased. */
function movedLabel(table: string, count: number): string {
  const known = MOVED_LABELS[table];
  if (known) return count === 1 ? known[0] : known[1];
  return table.replace(/_/g, ' ').toLowerCase();
}

/** Known tables in MOVED_LABELS order, then any other by name. */
function tableOrder(a: string, b: string): number {
  const rank = (table: string) => {
    const at = LABEL_ORDER.indexOf(table);
    return at === -1 ? LABEL_ORDER.length : at;
  };
  return rank(a) - rank(b) || a.localeCompare(b);
}

/**
 * The stored counts as one line: "<n> <label>" for each table with a moved count above 0,
 * joined by commas ("2 contacts, 1 opportunity"); NOTHING_MOVED when none moved.
 */
export function movedSummary(moved: Record<string, MovedCount> | null | undefined): string {
  const parts = Object.entries(moved ?? {})
    .map(([table, count]) => [table, Number(count?.moved ?? 0)] as const)
    .filter(([, n]) => Number.isFinite(n) && n > 0)
    .sort(([a], [b]) => tableOrder(a, b))
    .map(([table, n]) => `${n} ${movedLabel(table, n)}`);
  return parts.length ? parts.join(', ') : NOTHING_MOVED;
}

/** The note under a list cut at the route's limit, or null when every merge is listed. */
export function truncationNote(history: MergeHistory): string | null {
  return history.truncated ? `Showing the latest ${history.merges.length} of ${history.total_merges} merges.` : null;
}

/**
 * The merges into a record, for the record page: asked once per record, only on an account
 * or contact page and only for an Admin. Null until loaded, when there is no merge, and when
 * the request fails or is refused (no section, no error).
 */
export function useMergeHistory(fetcher: Fetch, object: ObjectKey, id: string, role: string | undefined): MergeHistory | null {
  const [history, setHistory] = useState<MergeHistory | null>(null);
  const asked = role === 'admin' && hasMergeHistory(object) && id !== '';
  useEffect(() => {
    setHistory(null);
    if (!asked || !hasMergeHistory(object)) return undefined;
    const controller = new AbortController();
    fetchMergeHistory(fetcher, object, id, { signal: controller.signal })
      .then((found) => {
        if (!controller.signal.aborted) setHistory(found.total_merges > 0 && found.merges.length > 0 ? found : null);
      })
      .catch(() => { /* No section when the merges can't load. */ });
    return () => controller.abort();
  }, [fetcher, object, id, asked]);
  return history;
}
